import { useState, useEffect } from 'react';

// Standalone consumer product. Local-storage only, single namespace.
// Growth is driven purely by CONSISTENCY (distinct active days) and is
// decoupled from any body/health data. Missing days pause growth but never
// remove progress — the same dormancy rule across every metaphor.
const KEY = 'companion_state';

export const CARE_ACTIONS = ['nourish', 'hydrate', 'weekly', 'mood'];
// Active days needed to reach stages 2..5.
export const MILESTONES = [3, 6, 10, 14];
export const COLLECTION_SIZE = 9;
export const SKY_SIZE = 18;

export function stageFromCount(n) {
  if (n >= MILESTONES[3]) return 5;
  if (n >= MILESTONES[2]) return 4;
  if (n >= MILESTONES[1]) return 3;
  if (n >= MILESTONES[0]) return 2;
  return 1;
}

function fresh() {
  return { metaphor: null, day: 1, actionsByDay: {} };
}

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...fresh(), ...JSON.parse(raw) } : fresh();
  } catch {
    return fresh();
  }
}

function dayHasCare(a) {
  return a && Object.values(a).some(Boolean);
}

export function useCompanion() {
  const [state, setState] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* quota */
    }
  }, [state]);

  const activeDays = Object.keys(state.actionsByDay || {})
    .filter((d) => dayHasCare(state.actionsByDay[d]))
    .map(Number);
  const activeCount = activeDays.length;
  const lastActive = activeDays.length ? Math.max(...activeDays) : 0;
  const stage = stageFromCount(activeCount);
  const todayActions = state.actionsByDay?.[state.day] || {};
  const todayActive = dayHasCare(todayActions);
  const gap = lastActive ? state.day - lastActive : 0;
  const dormant = activeCount > 0 && !todayActive && gap >= 2;
  const nextMilestone = MILESTONES.find((m) => m > activeCount) || null;

  const fullDay = { nourish: true, hydrate: true, weekly: true, mood: true };

  const log = (id) =>
    setState((s) => {
      const prev = s.actionsByDay[s.day] || {};
      return { ...s, actionsByDay: { ...s.actionsByDay, [s.day]: { ...prev, [id]: true } } };
    });
  const logToday = () =>
    setState((s) => ({ ...s, actionsByDay: { ...s.actionsByDay, [s.day]: { ...fullDay } } }));
  const advanceDay = () => setState((s) => ({ ...s, day: s.day + 1 }));
  const advanceAndLog = () =>
    setState((s) => {
      const day = s.day + 1;
      return { ...s, day, actionsByDay: { ...s.actionsByDay, [day]: { ...fullDay } } };
    });
  const skipDays = (n = 1) => setState((s) => ({ ...s, day: s.day + n }));
  const pickMetaphor = (m) => setState(() => ({ ...fresh(), metaphor: m }));
  const resetProgress = () => setState((s) => ({ ...fresh(), metaphor: s.metaphor }));
  const switchCompanion = () => setState(fresh());

  return {
    state,
    metaphor: state.metaphor,
    day: state.day,
    activeCount,
    stage,
    dormant,
    todayActions,
    lastActive,
    nextMilestone,
    log,
    logToday,
    advanceDay,
    advanceAndLog,
    skipDays,
    pickMetaphor,
    resetProgress,
    switchCompanion,
  };
}
